import { Component } from '@theme/component';
import { fetchConfig, debounce, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
  CartErrorEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange.bind(this), 300);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#debouncedOnChange.cancel();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    // Quantity events are broadcast at document level because the same component
    // powers both the basket page and the drawer. Only the component that owns
    // the control should submit the change; otherwise two cart surfaces can race
    // one another and make an apparently valid click look as though it was lost.
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // The Shopify/Horizon cart contract uses the current one-based line index
    // for cart/change requests. Because this component disables interaction
    // while a request is in flight, the line index cannot be re-numbered by a
    // second user action before the server response is applied.
    if (!line) return;

    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (quantity === 0) {
      this.#debouncedOnChange.cancel();
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      row: lineItemRow,
      quantity,
      action: 'change',
    });

    if (!(lineItemRow instanceof HTMLElement)) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    // A remove action must supersede any quantity event still waiting in the
    // debounce window. Otherwise the delayed quantity request can arrive after
    // the remove request and resurrect or mutate the wrong cart line.
    this.#debouncedOnChange.cancel();

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    this.updateQuantity({
      line,
      row: cartItemRowToRemove,
      quantity: 0,
      action: 'clear',
    });

    // Do not optimistically remove the row. The server response is authoritative
    // for cart line order, discounts, delivery state and empty-cart transitions.
    // Keeping the row mounted while the request is in flight prevents the drawer
    // from showing a blank/stale summary or making a line appear to disappear and
    // then return if the server resolves the cart differently.
    if (cartItemRowToRemove instanceof HTMLElement) {
      cartItemRowToRemove.setAttribute('aria-busy', 'true');
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The current one-based Shopify cart line index.
   * @param {HTMLTableRowElement} [config.row] - The live cart row that initiated the action.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const drawerScrollState = this.#captureDrawerScrollState();

    this.#disableCartItems();

    const { line, quantity, row } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // Keep the Ajax request aligned with Horizon's native cart implementation:
    // use the current one-based line index. The cart is locked until this
    // request completes, so no second user action can invalidate that index.
    const body = JSON.stringify({
      line,
      quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(config, parsedResponseText);
          return;
        }

        const sectionHtml = parsedResponseText.sections?.[this.sectionId];
        if (typeof sectionHtml !== 'string') {
          throw new Error(`Updated basket section "${this.sectionId}" was not returned`);
        }

        const newSectionHTML = new DOMParser().parseFromString(sectionHtml, 'text/html');

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        return this.#applySectionHtml(sectionHtml, newSectionHTML).then(() => {
          this.#restoreDrawerScrollState(drawerScrollState);
        });
      })
      .catch((error) => {
        console.error(error);
        this.dispatchEvent(
          new CartErrorEvent(
            this.sectionId,
            'The basket could not be updated. Please check your connection and try again.'
          )
        );
      })
      .finally(() => {
        if (row instanceof HTMLElement && row.isConnected) {
          row.removeAttribute('aria-busy');
        }

        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {{ line?: number, row?: HTMLTableRowElement }} config
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (config, parsedResponseText) => {
    const fallbackRow = config.line ? this.refs.cartItemRows[config.line - 1] : undefined;
    const row = config.row?.isConnected ? config.row : fallbackRow;
    const quantityInput = row?.querySelector('quantity-selector-component input');
    const cartItemError = row?.querySelector('.cart-item__error-text');
    const cartItemErrorContainer = row?.querySelector('.cart-items__error');

    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = quantityInput.defaultValue;
    }

    if (cartItemError instanceof HTMLElement && cartItemErrorContainer instanceof HTMLElement) {
      cartItemError.textContent = parsedResponseText.errors;
      cartItemErrorContainer.classList.remove('hidden');
    }

    this.dispatchEvent(new CartErrorEvent(this.sectionId, parsedResponseText.errors));
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    const drawerScrollState = this.#captureDrawerScrollState();

    if (event instanceof DiscountUpdateEvent) {
      await this.#refreshSection();
      this.#restoreDrawerScrollState(drawerScrollState);
      return;
    }

    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];

    if (cartItemsHtml) {
      await this.#applySectionHtml(cartItemsHtml);
    } else {
      await this.#refreshSection();
    }

    this.#restoreDrawerScrollState(drawerScrollState);
  };

  /**
   * Apply refreshed cart markup without replacing the open cart drawer itself.
   *
   * The drawer lives inside the header section. Morphing that whole section can
   * replace/reconnect the dialog owner and close the basket after a successful
   * quantity update. In drawer context we therefore morph only this
   * cart-items-component, while still syncing the trigger accessibility label
   * and empty-state class that live just outside it. The full basket page keeps
   * the normal section morph path.
   *
   * @param {string} sectionHtml
   * @param {Document} [parsedDocument]
   * @returns {Promise<void>}
   */
  async #applySectionHtml(sectionHtml, parsedDocument) {
    const drawer = this.closest('cart-drawer-component');

    if (!drawer) {
      await morphSection(this.sectionId, sectionHtml);
      return;
    }

    const nextDocument = parsedDocument ?? new DOMParser().parseFromString(sectionHtml, 'text/html');
    const nextDrawer = nextDocument.querySelector('cart-drawer-component');
    const nextCartItems = nextDrawer?.querySelector('cart-items-component');

    // Fall back to the standard renderer if the response shape is unexpected.
    if (!(nextCartItems instanceof HTMLElement)) {
      await morphSection(this.sectionId, sectionHtml);
      return;
    }

    const currentTrigger = drawer.querySelector('[ref="cartTrigger"]');
    const nextTrigger = nextDrawer?.querySelector('[ref="cartTrigger"]');
    const currentDialog = drawer.querySelector('dialog[ref="dialog"]');
    const nextDialog = nextDrawer?.querySelector('dialog[ref="dialog"]');

    if (currentTrigger instanceof HTMLElement && nextTrigger instanceof HTMLElement) {
      const nextLabel = nextTrigger.getAttribute('aria-label');
      if (nextLabel) currentTrigger.setAttribute('aria-label', nextLabel);

      // The drawer is intentionally kept mounted so it stays open while a cart
      // change is applied. That means the header trigger is not replaced by the
      // section morph. Resynchronise its visible basket bubble from the
      // authoritative server-rendered cart markup on every add/change/remove.
      const nextCountText = nextCartItems.querySelector('[ref="cartItemCount"]')?.textContent?.trim();
      const nextItemCount = nextCountText ? Number.parseInt(nextCountText, 10) : 0;
      const currentCartIcon = currentTrigger.querySelector('cart-icon');

      if (currentCartIcon && typeof currentCartIcon.renderCartBubble === 'function') {
        await currentCartIcon.renderCartBubble(
          Number.isFinite(nextItemCount) ? nextItemCount : 0,
          false,
          false
        );
      } else {
        // Defensive fallback for an un-upgraded cart-icon custom element.
        const nextCartIcon = nextTrigger.querySelector('cart-icon');
        if (currentCartIcon instanceof HTMLElement && nextCartIcon instanceof HTMLElement) {
          morph(currentCartIcon, nextCartIcon);
          if (typeof currentCartIcon.updatedCallback === 'function') currentCartIcon.updatedCallback();
        }
      }
    }

    if (currentDialog instanceof HTMLDialogElement && nextDialog instanceof HTMLDialogElement) {
      currentDialog.classList.toggle(
        'cart-drawer--empty',
        nextDialog.classList.contains('cart-drawer--empty')
      );
    }

    // morph() defaults to childrenOnly, so the live cart-items custom element,
    // its owning dialog and the drawer's open state are all preserved.
    morph(this, nextCartItems);
    this.updatedCallback();

    // The parsed response contains un-upgraded custom elements, so explicitly
    // resynchronise the live quantity controls after their values/min/max rules
    // have been morphed.
    for (const selector of this.querySelectorAll('quantity-selector-component')) {
      if (typeof selector.updatedCallback === 'function') selector.updatedCallback();
    }
  }

  /**
   * Refresh this cart section while keeping an open drawer mounted.
   * @returns {Promise<void>}
   */
  async #refreshSection() {
    if (!this.closest('cart-drawer-component')) {
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    const sectionHtml = await sectionRenderer.getSectionHTML(this.sectionId, false);
    await this.#applySectionHtml(sectionHtml);
  }

  /**
   * Capture the cart-drawer scroll position before the header section morphs.
   * The full cart page does not use this state.
   *
   * @returns {{ scrollTop: number, surface: 'root' | 'items' } | null}
   */
  #captureDrawerScrollState() {
    const drawer = this.closest('cart-drawer-component');
    const scrollRoot = drawer?.querySelector('[data-cart-drawer-scroll-root]');
    const itemScroll = drawer?.querySelector('[data-cart-drawer-scroll]');
    const rootOverflow = scrollRoot instanceof HTMLElement
      ? window.getComputedStyle(scrollRoot).overflowY
      : '';
    const scrollArea = rootOverflow === 'auto' || rootOverflow === 'scroll'
      ? scrollRoot
      : itemScroll;

    if (!(scrollArea instanceof HTMLElement)) return null;

    return {
      scrollTop: scrollArea.scrollTop,
      surface: scrollArea === scrollRoot ? 'root' : 'items',
    };
  }

  /**
   * Restore the cart-drawer scroll position after a section morph.
   *
   * @param {{ scrollTop: number, surface: 'root' | 'items' } | null} state
   */
  #restoreDrawerScrollState(state) {
    if (!state) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const selector = state.surface === 'root'
          ? 'cart-drawer-component [data-cart-drawer-scroll-root]'
          : 'cart-drawer-component [data-cart-drawer-scroll]';
        const scrollArea = document.querySelector(selector);

        if (scrollArea instanceof HTMLElement) {
          scrollArea.scrollTop = state.scrollTop;
        }
      });
    });
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
    this.setAttribute('aria-busy', 'true');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    this.setAttribute('aria-busy', 'false');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}